<template lang="html">
  <form class="u-form u-form--light" @submit.prevent="createSquad">
    <div v-if="!submitted">
      <div class="u-mt2 u-mb2">

        <div class="u-mt2 u-mb2">
          <label for="squad-first-name">
            Squad Name
            <span data-error v-show="errors.has('name')">{{ errors.first('name')}}</span>
          </label>
          <input id="squad-name" name="name" type="text" v-model="squad.name" v-validate="{ max: 200 }" />
          <p class="u-form-hint">
            If no squad name is provided, a default squad name will be created for you.
          </p>
        </div>

        <div class="u-mt2 u-mb2">
          <label for="squad-first-name">
            First Name<span aria-hidden="true">*</span>
            <span data-error v-show="errors.has('firstName')">{{ errors.first('firstName')}}</span>
          </label>
          <input id="squad-first-name" name="firstName" type="text" v-model="squad.firstName" v-validate="{ required: true }" required />
          <p class="u-form-hint">
            Provide the first name of the squad leader.
          </p>
        </div>

        <div class="u-mt2 u-mb2">
          <label for="squad-last-name">
            Last Name<span aria-hidden="true">*</span>
            <span data-error v-show="errors.has('lastName')">{{ errors.first('lastName')}}</span>
          </label>
          <input id="squad-last-name" name="lastName" type="text" v-model="squad.lastName" v-validate="{ required: true }" required />
          <p class="u-form-hint">
            Provide the last name of the squad leader.
          </p>
        </div>

        <div class="u-mt2 u-mb2">
          <label for="squad-zip">
            ZIP*
            <span data-error v-show="errors.has('zip')">{{ errors.first('zip')}}</span>
          </label>
          <input id="squad-zip" name="zip" type="text" autocomplete="off" v-model="squad.zip" v-validate="{ numeric: true, required: true }" required />
          <p class="u-form-hint">
            Provide the squad's zip code. This will be used to determine your squad's judicial district.
          </p>
        </div>

        <div v-if="noDistricts" class="u-mt2 u-mb2">
          <p role="alert" data-error>Sorry, there are no districts available for that ZIP code. Please check that your zip code is correctly entered, or try a different zip code.</p>
        </div>

        <fieldset v-if="!!districts.length" class="u-mt2 u-mb2">
          <legend>
            Select Your District:
          </legend>
          <ul>
            <li v-for="(district, index) in districts">
              <input name="district-id" v-model="squad.districtId" v-bind:value="district.node.id" v-bind:id="'district-id-' + district.node.id" type="radio">
              <label name="district-id" v-model="squad.districtId" v-bind:value="district.node.id" v-bind:for="'district-id-' + district.node.id">
                {{district.node.state_abbreviation}} - {{ district.node.name }} ({{ district.node.county_names }})
              </label>
            </li>
          </ul>
        </fieldset>

        <div class="u-mt2 u-mb2">
          <label for="squad-email">
            Email<span aria-hidden="true">*</span>
            <span data-error v-show="errors.has('email')">{{ errors.first('email') }}</span>
          </label>
          <input type="email" name="email" id="squad-email" v-model="squad.email" data-vv-delay="500" v-validate="{ required: true, email: true }" />
          <p class="u-form-hint">
            Provide an email address where future squad members can reach you. This information will not be displayed publicly.
          </p>
        </div>

        <div class="u-mt2 u-mb2">
          <label for="squad-phone">Phone</label>
          <input type="text" id="squad-phone" v-model="squad.phone" />
          <p class="u-form-hint">
            Provide a phone number where Color Of Change can reach you. This information will be not displayed publicly.
          </p>
        </div>

        <div class="u-mt2 u-mb2" v-if="squad.phone">
          <input id="squad-sms" v-bind:disabled="!squad.phone" type="checkbox" v-model="squad.sms" />
          <label for="squad-sms">I would like to receive updates via SMS</label>
        </div>

        <div class="u-mt2 u-mb2">
          <label for="squad-website">
            Website
            <span data-error v-show="errors.has('website')">{{ errors.first('website') }}</span>
          </label>
          <input type="text" name="website" id="squad-website" v-model="squad.website" v-validate="{ url: true, required: false}" />
          <p class="u-form-hint">
            Provide a website URL where future squad members can find your squad.
          </p>
        </div>

        <div class="u-mt2 u-mb2">
          <label for="squad-org">Organization Name</label>
          <input type="text" id="squad-org" v-model="squad.organizationName" />
          <p class="u-form-hint">
            If your squad is associated with an organization, provide the organization's name here.
          </p>
        </div>

        <div class="u-mt2 u-mb2">
          <!-- <button v-bind:disabled="!valid" type="submit" class="Button">Submit</button> -->
          <button type="submit" class="Button Button--outline">
            Submit
          </button>
        </div>
      </div>
    </div>
    <div v-else>
      <p>Thank you for creating a squad. You will be notified when your request has been approved.</p>
    </div>
  </form>
</template>

<script>
import debounce from "lodash/debounce";

import Vue from "vue/dist/vue.common.js";
import VeeValidate from "vee-validate";

import SquadsService from "../services/squads";
import SearchByZipService from "../services/search-by-zip";

const dictionary = {
  en: {
    custom: {
      firstName: {
        required: "Please provide your first name"
      },
      lastName: {
        required: "Please provide your last name"
      },
      email: {
        required: "Please provide an email address",
        email: "Please provide a valid email address"
      },
      zip: {
        required: "Please provide a ZIP code",
        numeric: "Please provide a valid ZIP code"
      }
    }
  }
};

Vue.use(VeeValidate);

export default {
  data() {
    return {
      squad: {
        name: null,
        firstName: "",
        lastName: "",
        zip: "",
        email: "",
        phone: null,
        website: null,
        sms: false,
        organizationName: null,
        districtId: null
      },
      districts: [],
      noDistricts: false,
      submitted: false
    };
  },
  computed: {
    zip() {
      return this.squad.zip;
    },
    valid() {
      if (
        this.fields.firstName.valid &&
        this.fields.lastName.valid &&
        this.fields.zip.valid &&
        this.fields.email.valid &&
        !!this.squad.districtId
      ) {
        return true;
      }
      return false;
    }
  },
  watch: {
    zip() {
      this.squad.districtId = null;
      this.listDistricts();
    },
    districts() {
      if (this.districts.length == 0 && this.fields.zip.valid) {
        this.noDistricts = true;
      } else {
        this.noDistricts = false;
      }
    }
  },
  created() {
    this.$validator.localize(dictionary);
  },
  methods: {
    listDistricts: debounce(function(event) {
      if (this.fields.zip.valid) {
        SearchByZipService.search(this.squad.zip, "District").then(response => {
          this.districts = response.data.searchByZip.edges;
        });
      } else {
        this.districts = [];
      }
    }, 300),
    createSquad() {
      const self = this;
      self.$validator
        .validateAll()
        .then(result => {
          if (self.valid) {
            SquadsService.create(self.squad).then(response => {
              self.submitted = true;
            });
          } else if (self.districts.length === 0) {
            // error due to no districts for the inputted zip code
            self.focusFirstError({ items: [ { field: 'zip' }] });
          } else {
            self.focusFirstError(self.errors);
          }
        })
        .catch(err => {
          console.error(err);
        });
    },
    focusFirstError: function(errors) {
      let field = errors.items[0].field;
      let elem = document.querySelectorAll('[name="' + field + '"]')[0];
      elem.focus();
      return false;
    },
  }
};
</script>



// WEBPACK FOOTER //
// squad-form.vue?22d1d8ca